export const DISPLAY_KEYS = ['video','falling','keyboard','sheet','chords'];
export const DISPLAY_LABELS = { video:'Video', falling:'Falling notes', keyboard:'Keyboard', sheet:'Staff', chords:'Chords' };
export const DISPLAY_ORIENTATIONS = ['portrait','landscape'];
export const KEYBOARD_PRESETS = [25,37,49,88];
export const LOOK_AHEAD_PRESETS = [4,6,10,15];
export const WIDGET_HEIGHTS = { video:[200,1000], falling:[160,1400], keyboard:[72,360], sheet:[96,600], chords:[48,300] };
export const PIANO_DISPLAYS = ['falling','keyboard'];
export const displayGroup = key => PIANO_DISPLAYS.includes(key) ? PIANO_DISPLAYS : [key];
export const DISPLAY_PRESETS = [
  { id:'video', label:'Video focus', icon:'film', description:'Give the lesson all the room', columns:[['video'],['falling','keyboard','sheet','chords']], views:['video'] },
  { id:'watch', label:'Watch & play', icon:'film', description:'Lesson beside your piano', columns:[['video'],['falling','keyboard','sheet','chords']], views:['video','falling','keyboard','chords'] },
  { id:'read', label:'Read music', icon:'music', description:'Staff above your keyboard', columns:[['sheet','falling','keyboard','chords'],['video']], views:['sheet','keyboard'] },
  { id:'piano', label:'Piano focus', icon:'piano', description:'More room for notes and keys', columns:[['video'],['falling','keyboard','sheet','chords']], views:['falling','keyboard'] },
  { id:'everything', label:'Everything', icon:'display', description:'All available widgets', columns:[['video'],['falling','keyboard','sheet','chords']], views:DISPLAY_KEYS },
];

const presetViews=(preset,available)=>preset.views.filter(key=>preset.id!=='everything'||!available||available[key]);

export function applyDisplayPreset(layout, id, available) {
  const preset=DISPLAY_PRESETS.find(preset=>preset.id===id);
  if(!preset)throw new Error('Unknown practice preset.');
  const views=presetViews(preset,available);
  return validateDisplayLayout({...layout,columns:preset.columns,enabled:Object.fromEntries(DISPLAY_KEYS.map(key=>[key,views.includes(key)])),widgetSizes:{}});
}

export function matchingDisplayPreset(layout, available) {
  if(Object.values(layout.widgetSizes||{}).some(size=>size.width!==100||size.height!==null))return null;
  return DISPLAY_PRESETS.find(preset=>JSON.stringify(preset.columns)===JSON.stringify(layout.columns)&&DISPLAY_KEYS.every(key=>layout.enabled[key]===presetViews(preset,available).includes(key)))?.id??null;
}

export const displayOrientation = (width, height) => width >= height ? 'landscape' : 'portrait';

export function defaultDisplayLayout(item) {
  const music = item?.musicSources?.find(source => source.id === item.activeMusicId);
  return { columns:[['video'],['falling','keyboard','sheet','chords']],
    enabled:{ video:item?.display !== 'music' || item?.media?.kind === 'music' && !item.video, falling:true, keyboard:true, sheet:false, chords:true, ...music?.views },
    keyLabels:'c', lookAhead:6, reviewMarkers:false, staffPlayhead:false, keyboardKeys:defaultKeyboardKeys(music?.notes), widgetSizes:{} };
}
export function validateDisplayLayout(layout) {
  if (!layout || !Array.isArray(layout.columns) || layout.columns.length !== 2 || !layout.columns.every(Array.isArray)) throw new Error('Invalid display columns.');
  const keys = layout.columns.flat();
  if (keys.length !== 5 || new Set(keys).size !== 5 || keys.some(key => !DISPLAY_KEYS.includes(key))) throw new Error('Each display must appear once in the layout.');
  if (!layout.enabled || DISPLAY_KEYS.some(key => typeof layout.enabled[key] !== 'boolean') || !['all','c','none'].includes(layout.keyLabels) || !LOOK_AHEAD_PRESETS.includes(layout.lookAhead)) throw new Error('Invalid display preferences.');
  if (layout.reviewMarkers !== undefined && typeof layout.reviewMarkers !== 'boolean') throw new Error('Invalid review marker preference.');
  if (layout.staffPlayhead !== undefined && typeof layout.staffPlayhead !== 'boolean') throw new Error('Invalid staff playback bar preference.');
  const keyboardKeys=layout.keyboardKeys ?? 37,widgetSizes=layout.widgetSizes ?? {};
  if (!KEYBOARD_PRESETS.includes(keyboardKeys)) throw new Error('Invalid keyboard range.');
  if (!widgetSizes || Array.isArray(widgetSizes) || typeof widgetSizes !== 'object') throw new Error('Invalid widget sizes.');
  for (const [key,size] of Object.entries(widgetSizes)) {
    const bounds=WIDGET_HEIGHTS[key];
    if (!bounds || !size || typeof size!=='object' || Array.isArray(size) ||
      !Number.isFinite(size.width) || size.width<40 || size.width>100 ||
      size.height!==null && (!Number.isFinite(size.height) || size.height<bounds[0] || size.height>bounds[1])) throw new Error('Invalid widget size.');
  }
  return linkPianoDisplays({ ...structuredClone(layout), reviewMarkers: layout.reviewMarkers ?? false, staffPlayhead: layout.staffPlayhead ?? false, keyboardKeys, widgetSizes:structuredClone(widgetSizes) });
}
function linkPianoDisplays(layout) {
  // Existing layouts follow the keyboard's position and width. Keep saved
  // heights independent, and repair layouts made before the views were linked.
  const column=layout.columns.findIndex(keys=>keys.includes('keyboard')),keys=layout.columns[column];
  if(keys[keys.indexOf('keyboard')-1]!=='falling') {
    layout.columns=layout.columns.map(keys=>keys.filter(key=>key!=='falling'));
    layout.columns[column].splice(layout.columns[column].indexOf('keyboard'),0,'falling');
  }
  const sizes=layout.widgetSizes??={},width=sizes.keyboard?.width??sizes.falling?.width;
  if(width!==undefined)for(const key of PIANO_DISPLAYS)sizes[key]={height:sizes[key]?.height??null,width};
  return layout;
}
export function validateItemDisplayLayouts(layouts) {
  if (!layouts || Array.isArray(layouts) || typeof layouts!=='object' || Object.keys(layouts).some(key=>!DISPLAY_ORIENTATIONS.includes(key))) throw new Error('Invalid orientation layouts.');
  return Object.fromEntries(Object.entries(layouts).map(([key,layout])=>[key,validateDisplayLayout(layout)]));
}
export function itemDisplayLayout(item, orientation, legacyLayout) {
  if (!DISPLAY_ORIENTATIONS.includes(orientation)) throw new Error('Invalid display orientation.');
  if (!item) return defaultDisplayLayout(null);
  item.displayLayouts ??= {};
  // Copy the old project layout once. Neither another item nor a rotation
  // should inherit subsequent edits made to the active orientation.
  const defaults=defaultDisplayLayout(item);
  for (const key of DISPLAY_ORIENTATIONS) item.displayLayouts[key] ??= validateDisplayLayout(legacyLayout ? {...legacyLayout,keyboardKeys:legacyLayout.keyboardKeys ?? defaults.keyboardKeys} : defaults);
  return linkPianoDisplays(item.displayLayouts[orientation]);
}
export function resizeDisplay(layout, key, size) {
  const next=validateDisplayLayout(layout);
  for(const member of displayGroup(key))next.widgetSizes[member]={height:next.widgetSizes[member]?.height??null,width:size.width};
  next.widgetSizes[key]=size;
  return validateDisplayLayout(next);
}
export function defaultKeyboardKeys(notes = []) {
  const pitches=notes.filter(note=>!note.rejected).map(note=>note.pitch);
  if(!pitches.length)return 37;
  const [low,high]=pitches.reduce(([low,high],pitch)=>[Math.min(low,pitch),Math.max(high,pitch)],[127,0]);
  return KEYBOARD_PRESETS.find(count=>count>=Math.max(37,high-low+1)) || 88;
}
export function keyboardRange(notes, count = 37) {
  if (count===88) return {low:21,high:108};
  const [min,max]=notes.length ? notes.reduce(([low,high],note)=>[Math.min(low,note.pitch),Math.max(high,note.pitch)],[127,0]) : [60,72];
  const center=(min+max)/2,black=new Set([1,3,6,8,10]);
  let low=48,distance=Infinity;
  for(let start=0;start<=128-count;start++) {
    if(black.has(start%12) || max-min<count && (start>min || start+count-1<max)) continue;
    const d=Math.abs(start+(count-1)/2-center);
    if(d<distance){low=start;distance=d;}
  }
  return {low,high:low+count-1};
}
export function moveDisplay(layout, key, column, index) {
  const next = validateDisplayLayout(layout),group=displayGroup(key);
  next.columns = next.columns.map(keys => keys.filter(value => !group.includes(value)));
  next.columns[column].splice(Math.max(0,Math.min(index,next.columns[column].length)),0,...group);
  return validateDisplayLayout(next);
}
export function shiftDisplay(layout, key, direction) {
  const next=validateDisplayLayout(layout),group=displayGroup(key),column=next.columns.findIndex(keys=>keys.includes(key));
  const index=next.columns[column].findIndex(key=>group.includes(key));
  if(direction==='left'||direction==='right')return moveDisplay(next,key,direction==='left'?0:1,index);
  const remaining=next.columns[column].filter(key=>!group.includes(key)),adjacent=remaining[direction==='up'?index-1:index];
  const distance=adjacent?displayGroup(adjacent).length:0;
  return moveDisplay(next,key,column,index+(direction==='up'?-distance:distance));
}
